<section class="catalog-shell">
  <app-ui-page-header
    title="Produtos"
    subtitle="Cadastre o que você vende: produtos, serviços, assinaturas e seus planos de cobrança."
    [divider]="false"
  >
    <div actions class="flex flex-wrap gap-2">
      @if (createMode() === 'closed') {
        <a appButton variant="secondary" routerLink="/produtos/importar" aria-label="Importar produtos via CSV">
          <ng-icon name="heroArrowUpTray" size="16" /> Importar CSV
        </a>
        <button appButton variant="primary" (click)="openCreate()" aria-label="Criar novo produto">
          <ng-icon name="heroPlus" size="18" /> Novo produto
        </button>
      }
    </div>
  </app-ui-page-header>

  <!-- ── Painel inline de criar produto ── -->
  @if (createMode() !== 'closed') {
    <section appUiPanelCard class="create-panel" aria-label="Novo produto">
      @if (createMode() === 'type') {
        <div class="create-panel__head">
          <div>
            <span appUiLabel class="mb-1 block uppercase">Novo produto</span>
            <h2>Que tipo de produto?</h2>
            <p class="create-panel__sub">Escolha o que melhor descreve o que você vende.</p>
          </div>
          <button appButton variant="ghost" size="sm" type="button" (click)="closeCreate()">
            <ng-icon name="heroXMark" size="16" aria-hidden="true" /> Fechar
          </button>
        </div>
        <div class="type-grid" role="group" aria-label="Tipos de produto">
          @for (t of productTypes; track t.key) {
            <button
              type="button"
              appUiOptionRow
              (click)="selectType(t.key)"
              [attr.aria-label]="'Selecionar ' + t.label"
            >
              <div appUiOptionRowIcon aria-hidden="true">
                <ng-icon [name]="t.icon" size="20" />
              </div>
              <div appUiOptionRowCopy>
                <strong>{{ t.label }}</strong>
                <small>{{ t.description }}</small>
              </div>
            </button>
          }
        </div>
      } @else {
        <div class="create-panel__head">
          <div class="create-panel__crumb">
            <button appButton variant="ghost" size="sm" type="button" (click)="backToTypes()" aria-label="Voltar para tipos">
              <ng-icon name="heroArrowLeft" size="14" /> Voltar
            </button>
            <div>
              <span appUiLabel class="mb-1 block uppercase">Novo {{ productTypeLabel(productDraft().type).toLowerCase() }}</span>
              <h2>Detalhes do produto</h2>
              <p class="create-panel__sub">Apenas o nome é obrigatório. Outros detalhes podem ser editados depois.</p>
            </div>
          </div>
          <button appButton variant="ghost" size="sm" type="button" (click)="closeCreate()">
            <ng-icon name="heroXMark" size="16" aria-hidden="true" /> Fechar
          </button>
        </div>
        <form class="create-panel__form" (ngSubmit)="submitCreate()">
          <label class="form-field">
            <span appUiLabel class="uppercase">Nome <span class="required">*</span></span>
            <input
              #nameInput
              appInput
              type="text"
              name="name"
              required
              [ngModel]="productDraft().name"
              (ngModelChange)="updateDraftField('name', $event)"
              placeholder="Ex: Curso Avançado de Excel"
              [disabled]="submitting()"
            />
          </label>
          <label class="form-field">
            <span appUiLabel class="uppercase">Descrição (opcional)</span>
            <textarea
              appTextarea
              name="description"
              [ngModel]="productDraft().description"
              (ngModelChange)="updateDraftField('description', $event)"
              placeholder="Resumo curto que aparece no catálogo público"
              rows="4"
              [disabled]="submitting()"
            ></textarea>
          </label>
          <div class="form-field">
            <span appUiLabel class="uppercase">Foto de capa (opcional)</span>
            <app-ui-file-picker
              variant="card"
              icon="heroPhoto"
              accept="image/jpeg,image/png,image/webp,image/gif"
              [fileName]="coverFile()?.name ?? ''"
              placeholder="Selecionar imagem de capa"
              [hint]="coverFile() ? 'Vira a capa. Mais imagens na tela do produto (até 8).' : 'JPEG, PNG, WEBP ou GIF · até 2 MB. Você pode adicionar mais depois.'"
              [disabled]="submitting()"
              (fileChange)="onCoverPicked($event)"
            />
          </div>
          <div class="form-actions">
            <button appButton variant="ghost" type="button" (click)="closeCreate()" [disabled]="submitting()">Cancelar</button>
            <button appButton variant="primary" type="submit" [disabled]="submitting() || !productDraft().name.trim()">
              {{ submitting() ? 'Criando…' : 'Criar e abrir' }}
            </button>
          </div>
        </form>
      }
    </section>
  }

  @if (createMode() === 'closed') {
    <!-- ── Filters bar ── -->
    <div appUiFilterBar>
      <div appUiFilterCluster>
        <div appUiFilterField class="min-w-0">
          <span appUiFilterLabel>Buscar</span>
          <input
            appInput
            type="search"
            class="h-9 w-52"
            placeholder="Buscar produtos…"
            [ngModel]="search()"
            (ngModelChange)="onSearchChange($event)"
            aria-label="Buscar produtos"
          />
        </div>

        <div appUiFilterField class="min-w-0 w-36">
          <span appUiFilterLabel>Status</span>
          <div appSelect [ngpSelectValue]="statusFilter()" (ngpSelectValueChange)="setStatus($event)" aria-label="Filtrar por status">
            <span>{{ statusOptions.find(o => o.value === statusFilter())?.label }}</span>
            <div *ngpSelectPortal appSelectDropdown>
              @for (opt of statusOptions; track opt.value) {
                <div appSelectOption [ngpSelectOptionValue]="opt.value">{{ opt.label }}</div>
              }
            </div>
          </div>
        </div>

        <div appUiFilterField class="min-w-0 w-40">
          <span appUiFilterLabel>Tipo</span>
          <div appSelect [ngpSelectValue]="typeFilter() ?? '__all__'" (ngpSelectValueChange)="setTypeFilter($event)" aria-label="Filtrar por tipo">
            <span>
              @if (typeFilter() === '__none__') { Sem tipo } @else if (typeFilter()) { {{ productTypeLabel(typeFilter()) }} } @else { Todos }
            </span>
            <div *ngpSelectPortal appSelectDropdown>
              <div appSelectOption [ngpSelectOptionValue]="'__all__'">Todos</div>
              @for (t of productTypes; track t.key) {
                <div appSelectOption [ngpSelectOptionValue]="t.key">{{ t.label }}</div>
              }
              <div appSelectOption [ngpSelectOptionValue]="'__none__'">Sem tipo</div>
            </div>
          </div>
        </div>

        <div appUiFilterField class="min-w-0 w-44">
          <span appUiFilterLabel>Ordenar</span>
          <div appSelect [ngpSelectValue]="sortValue()" (ngpSelectValueChange)="setSort($event)" aria-label="Ordenação">
            <span>{{ sortOptions.find(o => o.value === sortValue())?.label }}</span>
            <div *ngpSelectPortal appSelectDropdown>
              @for (opt of sortOptions; track opt.value) {
                <div appSelectOption [ngpSelectOptionValue]="opt.value">{{ opt.label }}</div>
              }
            </div>
          </div>
        </div>
      </div>

      <div appUiFilterActions>
        <div class="grid gap-1.5">
          <span appUiFilterLabel>Visualização</span>
          <div appToggleGroup ngpToggleGroupType="single" [ngpToggleGroupValue]="[viewMode()]" (ngpToggleGroupValueChange)="setViewMode($any($event)?.[0] ?? 'table')" role="group" aria-label="Visualização">
            <button appToggleGroupItem tone="accent" [ngpToggleGroupItemValue]="'table'" aria-label="Ver em tabela">
              <ng-icon name="heroListBullet" size="18" />
            </button>
            <button appToggleGroupItem tone="accent" [ngpToggleGroupItemValue]="'grid'" aria-label="Ver em grade">
              <ng-icon name="heroSquares2x2" size="18" />
            </button>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- ── Loading / empty / content ── -->
  @if (loading() && products().length === 0) {
    <app-ui-resource-grid [loading]="true" aria-live="polite">
      @for (i of [0,1,2,3,4,5]; track i) {
        <app-ui-resource-card-skeleton layout="mediaMeta" />
      }
    </app-ui-resource-grid>
  } @else if (products().length === 0 && createMode() === 'closed') {
    @if (hasFiltersApplied()) {
      <app-ui-empty-state icon="heroShoppingBag" title="Nenhum produto encontrado" description="Tente ajustar os filtros ou a busca.">
        <button actions appButton variant="secondary" type="button" (click)="clearFilters()">Limpar filtros</button>
      </app-ui-empty-state>
    } @else {
      <app-ui-empty-state icon="heroShoppingBag" title="Comece seu catálogo" description="Cadastre o primeiro produto, serviço ou assinatura que você vende.">
        <button actions appButton variant="primary" type="button" (click)="openCreate()">
          <ng-icon name="heroPlus" size="16" /> Criar primeiro produto
        </button>
      </app-ui-empty-state>
    }
  } @else if (products().length > 0) {
    <!-- Banner "selecionar todos do filtro" -->
    @if (allOnPageSelected() && !selection.allMatching() && total() > products().length) {
      <div class="select-all-banner">
        <span>Os {{ products().length }} desta página estão selecionados.</span>
        <button appButton variant="ghost" size="sm" type="button" (click)="selectAllMatching()">
          Selecionar todos os {{ total() }} do filtro
        </button>
      </div>
    }

    @if (viewMode() === 'table') {
      <div appUiDataTableShell>
        <table appUiDataTable appUiResizableColumns key="revenue-products">
          <thead appUiDataTableHead>
            <tr appUiDataTableRow>
              <th appUiDataTableHeaderCell class="w-10">
                <button appCheckbox [ngpCheckboxChecked]="allOnPageSelected()" (ngpCheckboxCheckedChange)="selectAllOnPage($any($event))" aria-label="Selecionar todos"></button>
              </th>
              <th appUiDataTableHeaderCell class="w-14">Foto</th>
              <th
                appUiDataTableHeaderCell
                appUiSortHeader
                sortKey="name"
                [activeKey]="sortBy()"
                [direction]="sortOrder()"
                (sortChange)="onColumnSort($event)"
                #nameSort="uiSortHeader"
              >
                Nome <ng-icon [name]="nameSort.iconName()" size="13" />
              </th>
              <th appUiDataTableHeaderCell>Tipo</th>
              <th appUiDataTableHeaderCell>Preço</th>
              <th appUiDataTableHeaderCell>Status</th>
              <th appUiDataTableHeaderCell class="text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            @for (product of products(); track product.id) {
              <tr appUiDataTableRow>
                <td appUiDataTableCell>
                  <button appCheckbox [ngpCheckboxChecked]="isSelected(product.id)" (ngpCheckboxCheckedChange)="toggleSelect(product.id)" [attr.aria-label]="'Selecionar ' + product.name"></button>
                </td>
                <td appUiDataTableCell>
                  <span class="thumb">
                    @if (product.cover_image?.url) {
                      <img [src]="product.cover_image!.url" alt="" loading="lazy" />
                    } @else {
                      <ng-icon [name]="productTypeIcon(product.type)" size="18" />
                    }
                  </span>
                </td>
                <td appUiDataTableCell>
                  <a class="row-name" [routerLink]="['/produtos', product.id]">{{ product.name }}</a>
                </td>
                <td appUiDataTableCell>{{ productTypeLabel(product.type) }}</td>
                <td appUiDataTableCell>{{ formatProductPrice(product) }}</td>
                <td appUiDataTableCell>
                  @if (product.status === 'archived') {
                    <span appBadge tone="neutral" size="xs">Arquivado</span>
                  } @else {
                    <span appBadge tone="success" size="xs">Ativo</span>
                  }
                  @if (product.in_use_by_active_sales) {
                    <span appBadge tone="info" size="xs" title="Em uso por vendas ativas">Em uso</span>
                  }
                </td>
                <td appUiDataTableCell class="text-right">
                  <a appButton variant="ghost" size="sm" [routerLink]="['/produtos', product.id]">Abrir</a>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else {
      <app-ui-resource-grid role="list" minColumnWidth="15rem">
        @for (product of products(); track product.id) {
          <app-product-card
            role="listitem"
            [product]="product"
            [selected]="isSelected(product.id)"
          >
            <span card-top class="card-checkbox">
              <button appCheckbox [ngpCheckboxChecked]="isSelected(product.id)" (ngpCheckboxCheckedChange)="toggleSelect(product.id)" [attr.aria-label]="'Selecionar ' + product.name"></button>
            </span>
            <ng-container card-badges>
              @if (product.status === 'archived') {
                <span appBadge tone="neutral" size="xs">Arquivado</span>
              } @else {
                <span appBadge tone="success" size="xs">Ativo</span>
              }
              @if (product.in_use_by_active_sales) {
                <span appBadge tone="info" size="xs" title="Em uso por vendas ativas">Em uso</span>
              }
            </ng-container>
            <div card-actions class="card-actions">
              <a appButton variant="ghost" size="sm" [routerLink]="['/produtos', product.id]">Abrir</a>
              <button appButton variant="ghost" size="sm" type="button" (click)="archive(product, $event)" [attr.aria-label]="'Arquivar ' + product.name">
                <ng-icon name="heroArchiveBoxXMark" size="14" /> Arquivar
              </button>
            </div>
          </app-product-card>
        }
      </app-ui-resource-grid>
    }

    <nav class="pagination" aria-label="Paginação de produtos">
      <span class="pagination__range">{{ pageRangeLabel() }}</span>
      <div class="pagination__actions">
        <button appButton variant="secondary" size="sm" type="button" [disabled]="!hasPrev() || loading()" (click)="goToPrevPage()" aria-label="Página anterior">
          <ng-icon name="heroChevronLeft" size="14" /> Anterior
        </button>
        <span class="pagination__page">Pág. {{ page() }} de {{ totalPages() || 1 }}</span>
        <button appButton variant="secondary" size="sm" type="button" [disabled]="!hasNext() || loading()" (click)="goToNextPage()" aria-label="Próxima página">
          Próxima <ng-icon name="heroChevronRight" size="14" />
        </button>
      </div>
    </nav>
  }

  <!-- ── Bulk action bar (flutuante) ── -->
  <app-ui-bulk-bar
    [count]="selectionCount()"
    [actions]="bulkActions"
    ariaLabel="Ações em massa de produtos"
    (action)="onBulkAction($event)"
    (clear)="selection.clear()"
  />
</section>
